/**
 * Font glyph filter
 * Strips characters from a string that are not present in the target font's glyph table,
 * so the editor never accepts text that the print-ready PDF cannot render.
 */

export type GlyphFilterResult = {
  filtered: string
  removed: string[]
}

// Whitespace is always allowed regardless of the font — TTF/OTF files routinely omit
// glyph entries for \n/\t/regular space, and stripping them would mangle layout.
const ALWAYS_ALLOWED_CODEPOINTS = new Set<number>([
  0x09, // tab
  0x0a, // line feed
  0x0d, // carriage return
  0x20, // space
])

const isAlwaysAllowed = (codepoint: number): boolean => ALWAYS_ALLOWED_CODEPOINTS.has(codepoint)

export const filterUnsupportedChars = (
  text: string,
  supported: Set<number> | undefined,
): GlyphFilterResult => {
  if (!supported) return { filtered: text, removed: [] }

  let filtered = ''
  const removed: string[] = []
  const seen = new Set<number>()

  for (const char of text) {
    const cp = char.codePointAt(0)
    if (cp === undefined) continue
    if (isAlwaysAllowed(cp) || supported.has(cp)) {
      filtered += char
    } else if (!seen.has(cp)) {
      seen.add(cp)
      removed.push(char)
    }
  }

  return { filtered, removed }
}
